import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';

import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {AuthService} from '../../../../services/auth.service';
import {DataService} from '../../../../services/data.service';
import {Budget, MarginV1} from '../../../../interfaces';
import {BudgetModel} from '../../../models/budget-model';
import {CommonModule} from '@angular/common';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';


@Component({
  selector: 'app-margin-form-v1',
  templateUrl: './margin-form-component-v1.component.html',
  styleUrls: ['./margin-form-component-v1.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule
  ]
})
export class MarginFormV1Component implements OnInit, OnDestroy {

  public formUpdating = false;
  @Input() kind: 'ITA' | 'USA' = 'ITA';
  public marginForm: UntypedFormGroup;
  private unsubscribe$ = new Subject();

  @Output() updateCostSummary = new EventEmitter<string>();

  constructor(private dataService: DataService,
              private fb: UntypedFormBuilder,
              private auth: AuthService) {
  }

  private _budget: Budget;

  get budget() {
    return this._budget;
  }

  @Input() set budget(b: Budget) {
    this._budget = b;

    if (this.marginForm) {
      this.patchForm();
    } else {
      console.log('Margin form not ready yet');
    }
  }

  private canGrantProjectAuth = false;
  private canEditBudget = false;
  private canEditOtherBudget = false;

  ngOnInit() {

    // no need to take default values from defaults as this is deprecated
    this.marginForm = this.fb.group({
      generalExpenses: [12, [Validators.min(0), Validators.max(100)]],
      profit: [20, [Validators.min(0), Validators.max(100)]],
      commission: [8.875, [Validators.min(0), Validators.max(100)]],
      customDuties: [0, [Validators.min(0), Validators.max(100)]]
    });
    this.patchForm();
    this.enableAutoSave();
    this.setPermissionHandler();
  }

  setPermissionHandler() {
    this.auth.userHasPermissions([
      'app.budget.auth.grant',
      'app.budget.write',
      'app.budget.edit.others'
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(val => {
        this.canGrantProjectAuth = val[0];
        this.canEditBudget = val[1];
        this.canEditOtherBudget = val[2];
        this.refreshFormEnabled();
      });
  }

  enableAutoSave() {
    this.marginForm.valueChanges
      .pipe(debounceTime(1000),
        takeUntil(this.unsubscribe$),
        distinctUntilChanged((val1, val2) => {
          return val1.generalExpenses === val2.generalExpenses &&
            val1.profit === val2.profit &&
            val1.commission === val2.commission &&
            val1.customDuties === val2.customDuties;
        }))
      .subscribe(async () => {
        await this.submit();
      });
  }

  patchForm() {

    if (this._budget && this._budget.margin) {

      if (this._budget.margin.generalExpenses !== undefined &&
        this._budget.margin.profit !== undefined &&
        (this._budget.margin as MarginV1).commission !== undefined &&
        (this._budget.margin as MarginV1).customDuties !== undefined) {
        console.log('margin-form: patch form: ', this._budget);
        this.marginForm.patchValue({
          generalExpenses: this._budget.margin.generalExpenses,
          profit: this._budget.margin.profit,
          commission: (this._budget.margin as MarginV1).commission,
          customDuties: (this._budget.margin as MarginV1).customDuties
        }, {emitEvent: false});
      }
    }
    this.refreshFormEnabled();
  }

  refreshFormEnabled() {
    if (!this.marginForm) return;

    if (BudgetModel.isMarginGenExpAndProfitEditable(
      this.budget,
      this.auth.currentUser,
      this.canGrantProjectAuth)) {
      this.marginForm.enable({emitEvent: false});
    } else {
      this.marginForm.disable({emitEvent: false});
    }

    // we use the same logic for this one
    if (BudgetModel.isRateEditable(
      this.budget,
      this.auth.currentUser,
      this.canGrantProjectAuth,
      this.canEditBudget,
      this.canEditOtherBudget)) {
      this.marginForm.get('commission').enable({emitEvent: false});
    } else {
      this.marginForm.get('commission').disable({emitEvent: false});
    }
  }

  async updateBudget(updateObj: { margin: MarginV1 }) {
    console.log('margin-form: updating budget margin...', updateObj.margin);
    this.formUpdating = true;
    this.marginForm.disable({emitEvent: false});

    try {
      await this.dataService.patchBudget(this._budget.id, updateObj);
      this.marginForm.enable({emitEvent: false});
    } catch (err) {
      console.log(err);
      this.marginForm.disable({emitEvent: false});
    } finally {
      this.formUpdating = false;
    }
  }

  async submit() {
    const margin: MarginV1 = {
      generalExpenses: this.marginForm.value.generalExpenses,
      customDuties: this.marginForm.value.customDuties,
      profit: this.marginForm.value.profit,
      commission: this.marginForm.value.commission
    };
    await this.updateBudget({margin});
    this.updateCostSummary.emit(this._budget.id);
  }

  public ngOnDestroy() {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }
}
